'use client';

import {
  useQuery,
  type QueryKey,
  type UseQueryOptions
} from '@tanstack/react-query';

import { useChainConfig } from '@/hooks/use-chain-config';
import { useWalletSession } from '@/hooks/use-wallet-session';
import { backOffOnError, shouldRetryApiRequest } from '@/lib/api-client';

/**
 * Every authenticated investor query answers the same four questions the
 * same way: is a wallet connected, which chain is it scoped to, may a
 * failure be retried, and how often should it poll. Eighteen hooks were
 * answering them by repetition, which meant a policy change had to be
 * applied eighteen times by hand — and any hook added later opted out of
 * all four by simply not mentioning them.
 *
 * This owns those answers. A feature hook supplies only what is genuinely
 * its own: the key, the fetcher, and its cadence.
 *
 * The scope gate is a privacy boundary, not a convenience: a query must not
 * run without an authenticated identity, and cached rows from one wallet
 * must never paint under another wallet's key.
 */

interface InvestorScope {
  /** Authenticated identity — non-null inside the fetcher by construction. */
  address: string;
  chainId: number;
}

/** The scope as the key factories take it, before the gate has passed. */
export interface InvestorKeyScope {
  address: string | undefined;
  chainId: number;
}

type QueryOptions<TData> = UseQueryOptions<TData, Error, TData, QueryKey>;

type Overrides<TData> = Pick<
  QueryOptions<TData>,
  | 'retry'
  | 'refetchInterval'
  | 'gcTime'
  | 'refetchOnWindowFocus'
  | 'refetchOnReconnect'
  | 'select'
  // Defaults to cadenceMs. Split it only where the server cache and the
  // poll genuinely differ, as the performance series does.
  | 'staleTime'
>;

interface InvestorQueryConfig<TData> extends Overrides<TData> {
  /** Must include the address and chain — see the feature's key factory. */
  key: (scope: InvestorKeyScope) => readonly unknown[];
  fetch: (scope: InvestorScope, signal: AbortSignal) => Promise<TData>;
  /**
   * Poll cadence, also used as the stale time. Defaults to the backend's
   * 60s recompute cache. Raise it for anything that fans out across rows —
   * the staging rate limiter is a hard budget.
   */
  cadenceMs?: number;
  /** Extra gate composed with the identity and chain gates. */
  enabled?: boolean;
  /**
   * Seeds this read from data already cached, such as one vault's row out
   * of a whole book the dashboard holds, so it costs no request. Called
   * with the same scope the key is built from, so a seed can never come
   * from another wallet or chain. Undefined seeds nothing.
   */
  initialData?: (scope: InvestorKeyScope) => TData | undefined;
  /** When the seed was read, so a stale seed is still refetched. */
  initialDataUpdatedAt?: (scope: InvestorKeyScope) => number | undefined;
  /**
   * Keep the previous page's rows while the next one loads, but only within
   * the same wallet and chain. Without the scope test this is a private-data
   * leak: the prior wallet's records paint under the new identity's key.
   */
  keepPreviousWithinScope?: boolean;
}

const DEFAULT_CADENCE_MS = 60 * 1000;

export function useInvestorQuery<TData>({
  key,
  fetch,
  cadenceMs = DEFAULT_CADENCE_MS,
  enabled = true,
  keepPreviousWithinScope = false,
  initialData,
  initialDataUpdatedAt,
  retry,
  refetchInterval,
  staleTime,
  ...rest
}: InvestorQueryConfig<TData>) {
  const { identityAddress, isReady } = useWalletSession();
  const { effectiveChainId } = useChainConfig();
  const scope: InvestorKeyScope = {
    address: identityAddress,
    chainId: effectiveChainId
  };

  const options: QueryOptions<TData> = {
    queryKey: key(scope),
    queryFn: ({ signal }) =>
      // Safe by construction: `enabled` below keeps this from running
      // without an authenticated identity.
      fetch({ address: identityAddress!, chainId: effectiveChainId }, signal),
    enabled: isReady && !!identityAddress && enabled,
    // Carried so the scope test below never has to read positions out of a
    // query key, which silently breaks the moment a key gains a parameter.
    meta: { scope: { address: identityAddress, chainId: effectiveChainId } },
    placeholderData: keepPreviousWithinScope
      ? (previousData, previousQuery) => {
          const previous = previousQuery?.meta?.scope as
            | InvestorKeyScope
            | undefined;
          return previous?.address === identityAddress &&
            previous?.chainId === effectiveChainId
            ? previousData
            : undefined;
        }
      : undefined,
    initialData: initialData ? () => initialData(scope) : undefined,
    initialDataUpdatedAt: initialDataUpdatedAt
      ? () => initialDataUpdatedAt(scope)
      : undefined,
    retry: retry ?? shouldRetryApiRequest,
    staleTime: staleTime ?? cadenceMs,
    // A deterministic failure re-asked every minute helps nobody and spends
    // the rate-limit budget other screens need.
    refetchInterval: refetchInterval ?? backOffOnError(cadenceMs),
    ...rest
  };

  return useQuery(options);
}
